import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useLocaleSettings } from '@/shared/i18n/I18nProvider'
import { createFormat } from '@/shared/i18n/use-format'
import { EmptyState } from '@/shared/layout/EmptyState'
import { ErrorState } from '@/shared/layout/ErrorState'
import { PageHeader } from '@/shared/layout/PageHeader'
import { Skeleton } from '@/shared/layout/Skeleton'
import { StickyActionBar } from '@/shared/layout/StickyActionBar'
import { SlotActions, useSlotColumns } from '@/shared/registry'
import { Button, cn, PageContainer } from '@/shared/ui'
import { Filter } from '@/shared/ui/icons'
import { useListFocus, useLoadMoreFocus, type FocusRef } from '@/shared/ui/use-list-focus'

import { ColumnChooser } from './ColumnChooser'
import { forDefinition, labelText, shownColumns, sortFieldOf, type SearchDefinition, type SearchPageHeader } from './definition'
import { ExportButton } from './ExportButton'
import { chipKey, FilterChips, inactiveChipKey } from './FilterChips'
import { FilterFieldEditor } from './FilterFieldEditor'
import { FilterLayout, filterInset, useFilterSidebar } from './FilterLayout'
import { InactiveOption } from './InactiveOption'
import { ResultsSummary } from './ResultsSummary'
import { ResultsTable, type TableColumn } from './ResultsTable'
import { SearchField } from './SearchField'
import { SearchScreenContext, savedSearchesSlot, type SearchScreenContextValue } from './search-screen-context'
import { readSearchState, writeSearchState, type FieldFilter, type SortState } from './search-state'
import { useFilterDrafts } from './use-filter-drafts'
import { useSearchResults } from './use-search-results'
import { useSearchState } from './use-search-state'

/**
 * A search screen, laid out as the mockups' `FindUsers`: the header; a toolbar with the free-text
 * field, below `lg` the Filters button, and the column chooser; a chip per applied filter; the
 * count and the viewer's sort; then, where the screen has filters, the filter sidebar beside the
 * results (`FilterLayout`), with Open / Save in it; the results, sorted by clicking their column
 * headers, never a "Sort by" select even where a mockup has one, with "Load more"; and, for a list
 * that exports, "Export results" in the action bar.
 * Everything a viewer sets is kept in the URL.
 */
export function SearchScreen<T>(props: {
  readonly definition: SearchDefinition<T>
  readonly header: SearchPageHeader
}) {
  const intl = useIntl()
  const { definition, header } = props
  const [urlState, update] = useSearchState()
  const state = useMemo(() => forDefinition(urlState, definition), [urlState, definition])
  const settings = useLocaleSettings()
  const timeZone = state.timeZone ?? settings.timeZone
  const format = useMemo(() => createFormat(settings.locale, timeZone), [settings.locale, timeZone])
  const results = useSearchResults(definition, state, timeZone)
  const slotColumns = useSlotColumns(definition.columnSlot ?? '', results.pages)

  const columns = useMemo<readonly TableColumn<T>[]>(
    () =>
      shownColumns(definition.columns, state.columns).map((column) => ({
        id: column.id,
        header: labelText(intl, column),
        cell: (row: T) => column.cell(row, format),
        sortField: sortFieldOf(column),
      })),
    [definition.columns, state.columns, intl, format],
  )

  const setQuery = useCallback((q: string) => update((current) => ({ ...current, q })), [update])
  const setSort = useCallback((next: SortState | null) => update((current) => ({ ...current, sort: next })), [update])
  const clearSort = useCallback(() => setSort(null), [setSort])
  const sortedBy = useMemo(() => {
    const chosen = state.sort
    const column = chosen === null ? undefined : definition.columns.find((candidate) => sortFieldOf(candidate) === chosen.field)
    return chosen === null || column === undefined
      ? null
      : intl.formatMessage({ id: chosen.descending ? 'search.sort.byDescending' : 'search.sort.byAscending' }, { column: labelText(intl, column) })
  }, [state.sort, definition.columns, intl])

  // Once results have shown, the table and its headers stay while new ones load, so a header
  // clicked to sort keeps focus rather than vanishing with the rows.
  const [hasShownRows, setShownRows] = useState(false)
  if (!hasShownRows && results.status === 'ready' && results.rows.length > 0) {
    setShownRows(true)
  }
  const reloading = results.status === 'loading' && hasShownRows
  const setColumns = useCallback(
    (next: readonly string[]) => update((current) => ({ ...current, columns: next })),
    [update],
  )
  const setTimeZone = useCallback(
    (next: string) => update((current) => ({ ...current, timeZone: next === settings.timeZone ? null : next })),
    [update, settings.timeZone],
  )
  // A removed chip's button leaves the page, and so does every chip on Clear All: focus goes to the
  // next chip, else the one before, else the shown Filters heading or button, rather than to the page's h1.
  const chipFocus = useListFocus()
  const { fallback: filtersControl } = chipFocus
  const drafts = useFilterDrafts(state.filters, update)
  const { reset: resetDrafts } = drafts

  const chipKeys = useMemo(
    () => [...(state.includeInactive ? [inactiveChipKey] : []), ...state.filters.map(chipKey)],
    [state.includeInactive, state.filters],
  )
  const removeFilter = useCallback(
    (removed: FieldFilter) => {
      chipFocus.removed(chipKeys, chipKey(removed))
      update((current) => ({ ...current, filters: current.filters.filter((filter) => !sameFilter(filter, removed)) }))
    },
    [chipFocus, chipKeys, update],
  )
  const clearAll = useCallback(() => {
    resetDrafts()
    update((current) => ({ ...current, filters: [], includeInactive: false }))
  }, [resetDrafts, update])
  const clearFilters = useCallback(() => {
    chipFocus.cleared()
    clearAll()
  }, [chipFocus, clearAll])
  const setIncludeInactive = useCallback(
    (includeInactive: boolean) => update((current) => ({ ...current, includeInactive })),
    [update],
  )
  const hideInactive = useCallback(() => {
    chipFocus.removed(chipKeys, inactiveChipKey)
    setIncludeInactive(false)
  }, [chipFocus, chipKeys, setIncludeInactive])
  const inactiveLabel = definition.inactive === undefined ? null : intl.formatMessage({ id: `search.inactive.${definition.inactive}` })
  const inactiveChip = useMemo(
    () => (inactiveLabel !== null && state.includeInactive ? { label: inactiveLabel, onRemove: hideInactive } : undefined),
    [inactiveLabel, state.includeInactive, hideInactive],
  )
  // Showing switched-off records counts as one applied filter, as its chip does.
  const appliedCount = state.filters.length + (inactiveLabel !== null && state.includeInactive ? 1 : 0)

  const sidebar = useFilterSidebar()
  const hasFilters = definition.filters.length > 0 || inactiveLabel !== null
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const inset = hasFilters && filterInset(sidebar)

  const { rowId } = definition
  const rowKeys = useMemo(() => results.rows.map((row) => rowId(row)), [results.rows, rowId])
  const moreFocus = useLoadMoreFocus(rowKeys, results.loadingMore)
  const { loadMore } = results
  const loadMoreClick = useCallback(() => {
    moreFocus.loading()
    loadMore()
  }, [moreFocus, loadMore])

  // Exactly the query the shown results loaded with, so a relative period exports the same range;
  // none until they have loaded.
  const loadedQuery = results.status === 'ready' ? results.query : null
  const exportPath = useMemo(() => {
    const columnIds = shownColumns(definition.columns, state.columns).map((column) => column.id)
    if (loadedQuery === null || columnIds.length === 0) {
      return null
    }
    const query = new URLSearchParams(loadedQuery)
    query.set('columns', columnIds.join(','))
    query.set('timeZone', timeZone)
    return `${definition.path}/export?${query.toString()}`
  }, [loadedQuery, definition.columns, definition.path, state.columns, timeZone])

  const query = writeSearchState(state).toString()
  // As the screen opened, before any slot loads: a viewer may search before the saved-search one does.
  const [opened] = useState(() => ({ query, isEmpty: query === '' }))
  const [hasSearched, setSearched] = useState(false)
  if (!hasSearched && query !== opened.query) {
    setSearched(true)
  }
  const isUnsearched = opened.isEmpty && !hasSearched && query === opened.query
  const screen = useMemo<SearchScreenContextValue>(
    () => ({
      screen: definition.screen,
      query,
      apply: (saved: string) => {
        resetDrafts()
        update(() => readSearchState(new URLSearchParams(saved)))
      },
      isUnsearched,
      loadedQuery,
    }),
    [definition.screen, query, update, isUnsearched, loadedQuery, resetDrafts],
  )

  const count =
    results.status === 'ready'
      ? intl.formatMessage(
          { id: results.hasMore ? 'search.results.firstCount' : 'search.results.count' },
          { count: results.rows.length },
        )
      : ''

  // A viewer who may not read the list, or whose list the API refuses with a code the screen names,
  // such as a missing parent record, is told so, with nothing to search, filter or export.
  const refusalId =
    results.status === 'forbidden'
      ? 'search.results.noAccess'
      : results.errorCode === null
        ? undefined
        : definition.errorMessages?.[results.errorCode]
  const refusal = refusalId === undefined ? null : intl.formatMessage({ id: refusalId })
  const hasSidebar = hasFilters && refusal === null

  const resultsArea = (
    <>
      {results.status === 'loading' && !reloading && loadingRows}
      {results.status === 'error' && refusal === null && (
        <ErrorState title={intl.formatMessage({ id: 'search.results.error' })} onRetry={results.retry} />
      )}
      {refusal !== null && <EmptyState>{refusal}</EmptyState>}
      {results.status === 'ready' && results.rows.length === 0 && (
        <EmptyState>{intl.formatMessage({ id: 'search.results.empty' })}</EmptyState>
      )}
      {(reloading || (results.status === 'ready' && results.rows.length > 0)) && (
        <>
          <ResultsTable
            caption={header.caption ?? header.title}
            columns={columns}
            slotColumns={slotColumns}
            rows={results.rows}
            rowId={definition.rowId}
            sort={state.sort}
            onSort={setSort}
            rowRef={moreFocus.row}
          />
          {reloading && <div className="mt-2">{loadingRows}</div>}
          {results.status === 'ready' && (results.hasMore || results.moreFailed) && (
            <div className="mt-4 flex flex-col items-center gap-2">
              {results.moreFailed && (
                <p role="alert" className="text-sm text-error-text">
                  {intl.formatMessage({ id: 'search.results.moreError' })}
                </p>
              )}
              <Button variant="outlined" size="small" onClick={loadMoreClick} busy={results.loadingMore}>
                {intl.formatMessage({ id: results.loadingMore ? 'search.results.loadingMore' : 'search.results.loadMore' })}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )

  return (
    <PageContainer>
      <SearchScreenContext value={screen}>
        <PageHeader
          icon={header.icon}
          title={header.title}
          {...(header.eyebrow === undefined ? {} : { eyebrow: header.eyebrow })}
          actions={header.actions}
        />
      </SearchScreenContext>

      {refusal === null && (
        <SearchScreenContext value={screen}>
          {/* The mockups' toolbar: the search field on the left, as wide as the filter sidebar below it. */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <SearchField value={state.q} onChange={setQuery} placeholder={header.searchPlaceholder} className="w-full sm:w-72 lg:w-[var(--filter-col)]" />
              {/* With filters, Open / Save sit in the sidebar. */}
              {!hasFilters && <SlotActions slot={savedSearchesSlot} />}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {hasFilters && !sidebar.isWide && (
                <FiltersButton count={appliedCount} isOpen={isDrawerOpen} onOpen={openDrawer} buttonRef={filtersControl} />
              )}
              <ColumnChooser
                columns={definition.columns}
                chosen={state.columns}
                onChoose={setColumns}
                timeZone={timeZone}
                onTimeZone={setTimeZone}
              />
            </div>
          </div>
        </SearchScreenContext>
      )}

      {refusal === null && (
        <div className={cn(inset)}>
          <FilterChips
            fields={definition.filters}
            filters={state.filters}
            inactive={inactiveChip}
            focus={chipFocus}
            onRemove={removeFilter}
            onClearAll={clearFilters}
          />
        </div>
      )}

      {/* Always present, so a screen reader hears each change: loading, then the count or the refusal. */}
      <ResultsSummary sortedBy={refusal === null ? sortedBy : null} onClearSort={clearSort} className={inset}>
        {count}
        {refusal !== null && <span className="sr-only">{refusal}</span>}
        {(results.status === 'loading' || results.loadingMore) && (
          <span className="sr-only">
            {intl.formatMessage({ id: results.loadingMore ? 'search.results.loadingMore' : 'search.results.loading' })}
          </span>
        )}
      </ResultsSummary>

      {hasSidebar ? (
        <FilterLayout
          sidebar={sidebar}
          filters={
            <>
              {/* Above the groups, as the mockups' filter checkboxes sit at the top of a Filters panel. */}
              {inactiveLabel !== null && (
                <div className="py-3">
                  <InactiveOption label={inactiveLabel} checked={state.includeInactive} onChange={setIncludeInactive} />
                </div>
              )}
              {definition.filters.map((field) => (
                <FilterFieldEditor
                  key={field.id}
                  field={field}
                  draft={drafts.draftFor(field)}
                  onChange={drafts.change}
                  timeZone={timeZone}
                  count={chosenCount(state.filters, field.id)}
                />
              ))}
            </>
          }
          savedSearches={
            <SearchScreenContext value={screen}>
              <SlotActions slot={savedSearchesSlot} />
            </SearchScreenContext>
          }
          activeCount={appliedCount}
          onClear={clearAll}
          isDrawerOpen={isDrawerOpen}
          onCloseDrawer={closeDrawer}
          {...(sidebar.isWide && { fallbackRef: filtersControl })}
        >
          {resultsArea}
        </FilterLayout>
      ) : (
        resultsArea
      )}

      {/* Only with an action in it: alone, its count would repeat the one above the results. */}
      {refusal === null && definition.exportable === true && (
        <StickyActionBar info={count}>
          <ExportButton path={exportPath} fallbackName={`${definition.screen}.csv`} />
        </StickyActionBar>
      )}
    </PageContainer>
  )
}

const loadingRows = (
  <div className="flex flex-col gap-2">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-10 w-full" />
  </div>
)

function sameFilter(a: FieldFilter, b: FieldFilter): boolean {
  return a.field === b.field && a.operator === b.operator && a.values.join('|') === b.values.join('|')
}

/** How many values a field's applied filter has chosen: each of a list's, else one. */
function chosenCount(filters: readonly FieldFilter[], field: string): number {
  const applied = filters.find((filter) => filter.field === field)
  if (applied === undefined) {
    return 0
  }
  return applied.operator === 'in' || applied.operator === 'notIn' ? applied.values.length : 1
}

/**
 * The mockups' FilterButton, below `lg` where the filters open as a drawer: outlined, or filled
 * reading "N applied" once any filter applies.
 */
function FiltersButton(props: {
  readonly count: number
  readonly isOpen: boolean
  readonly onOpen: () => void
  /** Where focus goes once the last chip is removed. */
  readonly buttonRef: FocusRef
}) {
  const intl = useIntl()
  const { count, isOpen, onOpen, buttonRef } = props
  return (
    <Button
      ref={buttonRef}
      variant={count > 0 ? 'contained' : 'outlined'}
      size="small"
      onClick={onOpen}
      aria-expanded={isOpen}
      {...(count > 0 && { 'aria-label': intl.formatMessage({ id: 'search.filters.activeLabel' }, { count }) })}
    >
      <Filter size={16} />
      {count > 0 ? intl.formatMessage({ id: 'search.filters.active' }, { count }) : intl.formatMessage({ id: 'search.filters' })}
    </Button>
  )
}
